import { type Point } from "@common/declarations";
import { useUpdated } from "@common/lib/hooks";
import { isEqual } from "lodash";
import * as React from "react";
import { adjustPosition, AllowOffScreen, type Boundaries, type LeftTop } from "./lib";

export type InitialPosition = {
  left?: "center" | number;
  top?: number;
};
export interface DraggableHOCProps {
  allowOffScreen?: number;
  boundaries?: Boundaries;
  initialPosition?: InitialPosition;
  isPinned?: boolean;
  onDragStart?: (el: Element, position: LeftTop, e: MouseEvent) => void;
  onDragged?: (position: LeftTop, isPin: boolean, e?: MouseEvent) => void;
  onDragEnd?: (el: Element, position: LeftTop, e?: MouseEvent) => void;
}

export type DraggableHOCComponentProps<TElement extends HTMLElement> = {
  ref: React.RefObject<TElement | null>;
  onResized: (width: number | null, height: number | null) => void;
  pin: () => void;
  style: React.CSSProperties;
  unpin: () => void;
};

type State = {
  isDragging: boolean;

  left?: number;
  top?: number;

  pinRight: number | null;
  pinBottom: number | null;
};

const DraggableHOC =
  <TElement extends HTMLElement, OriginalProps extends { style?: React.CSSProperties }>(
    Cmp: React.ComponentType<OriginalProps & DraggableHOCComponentProps<TElement>>,
    handleSelector?: string,
    ignoreSelectors?: string[]
  ): React.ComponentType<DraggableHOCProps & OriginalProps> =>
  (props: OriginalProps & DraggableHOCProps) => {
    const ref = React.useRef<TElement | null>(null);

    const mousePosition = React.useRef<Point>({ x: 0, y: 0 });

    const [state, setState] = React.useState<State>({
      isDragging: false,
      pinBottom: null,
      pinRight: null
    });

    const getContainer = () => {
      return ref.current!;
    };

    const getHandle = (): HTMLElement | null => {
      const container = getContainer();
      if (!container) return null;
      return handleSelector ? container.querySelector(handleSelector) : container;
    };

    const ensureInBoundaries = (lt: Partial<LeftTop>) => {
      const container = getContainer();
      if (!container) return;

      const alt = adjustPosition(
        { left: lt.left ?? 0, top: lt.top ?? 0 },
        container.getBoundingClientRect(),
        props.allowOffScreen || AllowOffScreen.None,
        props.boundaries
      );

      if (!isEqual(alt, lt)) {
        setState((x) => ({ ...x, ...alt }));
      }

      return alt;
    };

    const getLocation = (p: InitialPosition): Partial<LeftTop> => ({
      left: p.left === "center" ? (window.innerWidth - getContainer().clientWidth) / 2 : p.left,
      top: p.top
    });

    const pin = () => {
      const container = getContainer();

      const { right = 0, bottom = 0 } = props.boundaries ?? {
        right: 0,
        bottom: 0
      };
      const lt: LeftTop = {
        left: window.innerWidth - right - container.clientWidth,
        top: window.innerHeight - bottom - container.clientHeight
      };
      setState((x) => ({
        ...x,
        pinBottom: bottom,
        pinRight: right,
        ...lt
      }));

      ensureInBoundaries(lt);
    };

    const unpin = () => {
      setState((x) => ({ ...x, pinBottom: null, pinRight: null }));
    };

    React.useEffect(() => {
      const handle = getHandle();
      if (!handle) return;

      const onMouseMove = (e: MouseEvent) => {
        if (Math.abs(e.clientX - mousePosition.current.x) < 2 && Math.abs(e.clientY - mousePosition.current.y) < 2)
          return;

        let lt: LeftTop = {
          left: e.clientX - mousePosition.current.x,
          top: e.clientY - mousePosition.current.y
        };

        setState((x) => ({
          ...x,
          ...lt,
          isDragging: true
        }));

        lt = ensureInBoundaries(lt)!;

        const container = getContainer();

        const { right = 0, bottom = 0 } = props.boundaries ?? {
          right: 0,
          bottom: 0
        };

        const isPin =
          window.innerWidth - (lt.left + container.clientWidth) <= right &&
          window.innerHeight - (lt.top + container.clientHeight) <= bottom;
        props.onDragged && props.onDragged(lt, isPin, e);
      };

      const onMouseUp = (e: MouseEvent) => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);

        document.body.style.userSelect = "";

        if (state.isDragging) {
          if (props.onDragEnd) {
            const left = e.clientX - mousePosition.current.x;
            const top = e.clientY - mousePosition.current.y;

            props.onDragEnd(getContainer(), { left, top }, e);
          }

          setState((x) => ({ ...x, isDragging: false }));
        }
      };

      const onMouseDown = (e: MouseEvent) => {
        const handle = getHandle();

        if (
          !e.target ||
          e.button !== 0 ||
          (ignoreSelectors ?? []).some((x) => {
            const target = e.target as HTMLElement;
            if (target.matches(x)) return true;

            const parent = target.closest(x);
            return parent && parent !== handle;
          })
        )
          return;

        const container = getContainer();

        mousePosition.current.x = e.clientX - container.offsetLeft;
        mousePosition.current.y = e.clientY - container.offsetTop;

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        document.body.style.userSelect = "none";

        props.onDragStart &&
          props.onDragStart(container, { left: mousePosition.current.x, top: mousePosition.current.y }, e);
      };

      const onResize = (e: MouseEvent) => {
        ensureInBoundaries(state);
      };

      handle.style.cursor = "move";
      handle.addEventListener("mousedown", onMouseDown);
      window.addEventListener("resize", onResize);

      const { initialPosition } = props;
      if (initialPosition) {
        const location = getLocation(initialPosition);
        setState((x) => ({ ...x, ...location }));
        ensureInBoundaries(location);
      }

      if (props.isPinned) {
        pin();
      }

      return () => {
        if (handle) {
          handle.style.cursor = "";
          handle.removeEventListener("mousedown", onMouseDown);
          window.removeEventListener("resize", onResize);
        }
      };
    }, [ref.current]);

    useUpdated(() => {
      props.isPinned ? pin() : unpin();
    }, [props.isPinned]);

    const onContentResize = () => ensureInBoundaries(state);

    const { allowOffScreen, boundaries, initialPosition, onDragged, onDragEnd, onDragStart, style, ...originalProps } =
      props;
    const { left, top, pinBottom, pinRight } = state;

    const finalStyle: React.CSSProperties =
      pinBottom && pinRight
        ? {
            ...style,
            position: "absolute",
            bottom: pinBottom,
            right: pinRight
          }
        : {
            ...style,
            left,
            top
          };

    return (
      <Cmp
        {...(originalProps as OriginalProps)}
        ref={ref}
        pin={pin}
        unpin={unpin}
        onResized={onContentResize}
        style={finalStyle}
      />
    );
  };
export default DraggableHOC;
